// Project settings (RF-PROJ-01/04/05, RF-CFG-14): PATCH with expected_version and explicit
// 409 handling, the readiness check (202 + operation polling, its card showing the last
// stored result and when it was checked — Task 12) and the project routing override /
// Maestro override with the effective configuration preview. `?field=repository_url|base_branch`
// (the assistant's `open_settings_form` action, R19) focuses and scrolls to that field. A project without a checkout
// gets "Connect repository" (GitHub onboarding) with its provisioning progress; `?connect=1` opens that dialog.
// Workspaces, environments and access are listed as later-phase structure without simulated tests.
import { useEffect, useRef, useState, type FormEvent } from "react";
import { useSearchParams } from "react-router";
import { AUTONOMY_PRESETS, DELIVERY_TARGETS } from "@maestros/contracts/enums";
import type { AutonomyPreset, DeliveryTarget, ProjectSummary } from "@maestros/contracts";
import { useI18n } from "../../i18n/index.tsx";
import { getOperation, updateProject, validateProject } from "../../api/endpoints.ts";
import { isApiError } from "../../api/client.ts";
import type { OperationRecord, ProjectDetailResponse } from "../../api/types.ts";
import { getProvision } from "../../api/endpoints-onboarding.ts";
import { useAsync, type AsyncState } from "../../lib/useAsync.ts";
import { useOrgRole } from "../../features/config/shared.tsx";
import { GithubProjectDialog } from "../../features/onboarding/GithubProjectDialog.tsx";
import { ProvisionProgress } from "../../features/onboarding/ProvisionProgress.tsx";
import { Button } from "../../components/Button.tsx";
import { SelectField, TextAreaField, TextField } from "../../components/Field.tsx";
import { contentLanguageChoices } from "../../features/content-language/languages.ts";
import { Banner, UnavailableTag, localErrorMessage } from "../../components/Feedback.tsx";
import { Pill } from "../../components/Status.tsx";
import { useToast } from "../../components/Toast.tsx";
import { GitBranchIcon, RefreshIcon, ShieldIcon } from "../../components/icons.tsx";
import { newestCheckedAt } from "./ExecutionControls.tsx";
import { ProjectRoutingPanel } from "./ProjectRoutingPanel.tsx";
import { DeleteProjectSection } from "./DeleteProjectSection.tsx";
import { TestCommandCard } from "../../features/blocks/TestCommandCard.tsx";
import { DependenciesCard } from "../../features/dependencies/DependenciesCard.tsx";
import { readinessCauseText } from "../../features/blocks/explain.ts";
import type { WorkspaceRow } from "../../api/endpoints-blocks.ts";

interface FormState {
  name: string;
  description: string;
  repository_url: string;
  base_branch: string;
  delivery_target: DeliveryTarget;
  autonomy_preset: AutonomyPreset;
  auto_execute: boolean;
  max_concurrent_tasks: number;
  timezone: string;
  /** D37.3: "" = inherit the organization's content language. */
  content_language: string;
}

function fromProject(p: ProjectSummary): FormState {
  return {
    name: p.name,
    description: p.description,
    repository_url: p.repository_url ?? "",
    base_branch: p.base_branch,
    delivery_target: p.delivery_target,
    autonomy_preset: p.autonomy_preset,
    auto_execute: p.auto_execute,
    max_concurrent_tasks: p.max_concurrent_tasks,
    timezone: p.timezone,
    content_language: p.content_language ?? "",
  };
}

function ReadinessCheck({ projectId, stored }: { projectId: string; stored: AsyncState<ProjectDetailResponse> }) {
  const { t, formatRelative } = useI18n();
  // The last stored result (Task 12): shown right away, and reloaded once a fresh run succeeds — the operation
  // result itself carries no timestamp per check, only the persisted project does. `stored` is fetched once by
  // the page (fix round 1, minor) and shared with the header badge, instead of a second fetch here; the page's
  // own realtime subscription also keeps it fresh after `project.validation.completed`.
  // 202 AcceptedOperation first, then the operation record polled from /v1/operations/{id}.
  const [op, setOp] = useState<{ status: OperationRecord["status"]; error: OperationRecord["error"] } | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const timer = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    },
    [],
  );

  const poll = (id: string, attempt: number) => {
    timer.current = window.setTimeout(async () => {
      try {
        const next = await getOperation(id);
        setOp({ status: next.status, error: next.error });
        if (next.status === "accepted" || next.status === "running") poll(id, attempt + 1);
        else {
          setBusy(false);
          if (next.status === "succeeded") stored.reload();
        }
      } catch (e) {
        setError(e);
        setBusy(false);
      }
    }, Math.min(5000, 500 * 2 ** Math.min(attempt, 4)));
  };

  const start = async () => {
    setBusy(true);
    setError(null);
    try {
      const accepted = await validateProject(projectId);
      setOp({ status: accepted.status, error: null });
      if (accepted.status === "accepted" || accepted.status === "running") poll(accepted.operation_id, 0);
      else {
        setBusy(false);
        stored.reload();
      }
    } catch (e) {
      setError(e);
      setBusy(false);
    }
  };

  const checks = stored.error ? [] : (stored.data?.readiness ?? []);
  const checkedAt = newestCheckedAt(checks);

  return (
    <section className="card" aria-labelledby="readiness-title">
      <div className="card-header">
        <ShieldIcon size={18} />
        <h2 id="readiness-title" className="card-title" style={{ flex: 1 }}>
          {t("projectSettings.readiness")}
        </h2>
        <Button size="sm" icon={<RefreshIcon size={14} />} onClick={() => void start()} loading={busy}>
          {t("projectSettings.runReadiness")}
        </Button>
      </div>
      <div className="card-body stack" aria-live="polite">
        <p className="muted">{t("projectSettings.readinessBody")}</p>
        <p className="subtle" data-testid="readiness-last-checked">
          {checkedAt !== null ? t("projectSettings.lastChecked", { when: formatRelative(checkedAt) }) : t("projectSettings.neverChecked")}
        </p>
        {error ? (
          <Banner tone="error" role="alert">
            {localErrorMessage(error, t)}
          </Banner>
        ) : null}
        {op?.error ? (
          <Banner tone="error" role="alert">
            {op.error.message}
          </Banner>
        ) : null}
        {op ? (
          <p>
            {t("projectSettings.operationStatus")}{" "}
            <Pill
              tone={op.status === "succeeded" ? "success" : op.status === "failed" ? "danger" : "info"}
              icon={op.status === "succeeded" ? "check" : op.status === "failed" ? "x" : "spinner"}
            >
              {t(`operation.${op.status}`)}
            </Pill>
          </p>
        ) : null}
        {checks.length > 0 ? (
          <ul className="plain-list" data-testid="readiness-checks">
            {checks.map((c) => (
              <li key={c.check} className="row row--wrap" data-check={c.check} data-status={c.status}>
                <Pill tone={c.status === "passed" ? "success" : c.status === "failed" ? "danger" : "neutral"} icon={c.status === "passed" ? "check" : c.status === "failed" ? "x" : "circle"}>
                  {t(`readiness.check.${c.check}` as "readiness.check.repository")}
                </Pill>
                {c.cause ? <span className="subtle user-content">{readinessCauseText(t, c.cause)}</span> : null}
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </section>
  );
}

/**
 * "Connect repository" (existing-project onboarding, spec §3.1): shown while the project has no workspace (no
 * checkout) or once its latest provision was still running when the tab opened (a failed one only without a checkout),
 * so the progress stays in view until the readiness result; a failure also offers the dialog to pick another connection
 * or repository. `?connect=1` (the assistant's `connect_repository` action) opens the dialog for organization admins.
 */
function ConnectRepository({ project, detail }: { project: ProjectSummary; detail: AsyncState<ProjectDetailResponse> }) {
  const { t } = useI18n();
  const canConnect = useOrgRole().atLeast("admin");
  const [searchParams, setSearchParams] = useSearchParams();
  const connectParam = searchParams.get("connect") === "1";
  const [connecting, setConnecting] = useState(false);
  const provision = useAsync((s) => getProvision(project.id, s), [project.id]);
  const latest = provision.data?.provision ?? null;
  const workspaces = (detail.data as { workspaces?: unknown[] } | undefined)?.workspaces;
  const noCheckout = detail.data !== undefined && (workspaces?.length ?? 0) === 0;
  // A failed provision only matters while the project still has no checkout (an old failure never nags a working one).
  const tracking = latest !== null && latest.state !== "done" && (latest.state !== "failed" || noCheckout);

  useEffect(() => {
    if (connectParam && canConnect) setConnecting(true);
  }, [connectParam, canConnect]);

  const close = () => {
    setConnecting(false);
    provision.reload();
    if (connectParam) {
      const next = new URLSearchParams(searchParams);
      next.delete("connect");
      setSearchParams(next, { replace: true });
    }
  };

  if (!noCheckout && !tracking && !connecting) return null;
  return (
    <section className="card" aria-labelledby="connect-repository-title">
      <div className="card-header">
        <GitBranchIcon size={18} />
        <h2 id="connect-repository-title" className="card-title">
          {t("githubOnboarding.connectTitle")}
        </h2>
      </div>
      <div className="card-body stack">
        {tracking ? (
          <ProvisionProgress key={latest.id} projectId={project.id} onDone={detail.reload} {...(canConnect ? { onReconnect: () => setConnecting(true) } : {})} />
        ) : (
          <>
            <p className="muted">{t("githubOnboarding.cardBody")}</p>
            {canConnect ? (
              <div>
                <Button variant="primary" icon={<GitBranchIcon size={16} />} onClick={() => setConnecting(true)}>
                  {t("githubOnboarding.connectTitle")}
                </Button>
              </div>
            ) : (
              <p className="field-hint">{t("githubOnboarding.adminOnly")}</p>
            )}
          </>
        )}
      </div>
      <GithubProjectDialog open={connecting} onClose={close} mode="connect" project={project} />
    </section>
  );
}

export function ProjectSettingsTab({
  project,
  onSaved,
  detail,
}: {
  project: ProjectSummary;
  onSaved: () => void;
  detail: AsyncState<ProjectDetailResponse>;
}) {
  const { t } = useI18n();
  const { toast } = useToast();
  const [form, setForm] = useState<FormState>(() => fromProject(project));
  const [baseVersion, setBaseVersion] = useState(project.version);
  const [conflict, setConflict] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const dirty = JSON.stringify(form) !== JSON.stringify(fromProject(project));

  // `?field=repository_url|base_branch` (the assistant's `open_settings_form` action, R19): focus and
  // scroll to that field once, so the fix the assistant proposed lands exactly where the user can make it.
  const [searchParams] = useSearchParams();
  const focusField = searchParams.get("field");
  const repositoryFieldRef = useRef<HTMLInputElement>(null);
  const baseBranchFieldRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const target = focusField === "repository_url" ? repositoryFieldRef.current : focusField === "base_branch" ? baseBranchFieldRef.current : null;
    if (!target) return;
    target.scrollIntoView({ block: "center" });
    target.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusField]);

  // Adopt server changes when the form is pristine; never overwrite pending edits.
  useEffect(() => {
    if (!dirty) {
      setForm(fromProject(project));
      setBaseVersion(project.version);
    } else if (project.version !== baseVersion) {
      setConflict(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project.version]);

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setError(new Error(t("validation.required")));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await updateProject(project.id, {
        name: form.name.trim(),
        description: form.description,
        repository_url: form.repository_url.trim() || null,
        base_branch: form.base_branch.trim() || "main",
        delivery_target: form.delivery_target,
        autonomy_preset: form.autonomy_preset,
        auto_execute: form.auto_execute,
        max_concurrent_tasks: form.max_concurrent_tasks,
        timezone: form.timezone,
        content_language: form.content_language || null,
        expected_version: baseVersion,
      });
      setConflict(false);
      toast(t("projectSettings.saved"), "success");
      onSaved();
    } catch (err) {
      if (isApiError(err) && err.code === "version_conflict") setConflict(true);
      else setError(err);
    } finally {
      setBusy(false);
    }
  };

  const loadLatest = () => {
    setForm(fromProject(project));
    setBaseVersion(project.version);
    setConflict(false);
    onSaved();
  };

  return (
    <div className="stack">
    <ConnectRepository project={project} detail={detail} />
    <div className="settings-columns">
      <form className="card" onSubmit={onSubmit} noValidate aria-labelledby="project-settings-title">
        <div className="card-header">
          <h2 id="project-settings-title" className="card-title">
            {t("projectSettings.title")}
          </h2>
        </div>
        <div className="card-body stack">
          {conflict ? (
            <Banner
              tone="warning"
              role="alert"
              title={t("projectSettings.conflictTitle")}
              action={
                <Button size="sm" onClick={loadLatest}>
                  {t("projectSettings.loadLatest")}
                </Button>
              }
            >
              {t("projectSettings.conflictBody")}
            </Banner>
          ) : null}
          {error ? (
            <Banner tone="error" role="alert">
              {localErrorMessage(error, t)}
            </Banner>
          ) : null}
          <TextField label={t("project.name")} value={form.name} onChange={(e) => set("name", e.target.value)} maxLength={120} required />
          <TextAreaField label={t("project.description")} value={form.description} onChange={(e) => set("description", e.target.value)} rows={3} maxLength={2000} />
          <div className="form-grid">
            <TextField ref={repositoryFieldRef} label={t("project.repository")} value={form.repository_url} onChange={(e) => set("repository_url", e.target.value)} spellCheck={false} />
            <TextField ref={baseBranchFieldRef} label={t("project.baseBranch")} value={form.base_branch} onChange={(e) => set("base_branch", e.target.value)} spellCheck={false} />
            <SelectField label={t("project.deliveryTarget")} value={form.delivery_target} onChange={(e) => set("delivery_target", e.target.value as DeliveryTarget)}>
              {DELIVERY_TARGETS.map((d) => (
                <option key={d} value={d}>
                  {t(`delivery.${d}`)}
                </option>
              ))}
            </SelectField>
            <SelectField label={t("project.autonomy")} value={form.autonomy_preset} onChange={(e) => set("autonomy_preset", e.target.value as AutonomyPreset)}>
              {AUTONOMY_PRESETS.map((a) => (
                <option key={a} value={a}>
                  {t(`autonomy.${a}`)}
                </option>
              ))}
            </SelectField>
            <TextField
              label={t("project.maxConcurrent")}
              type="number"
              min={1}
              max={50}
              value={form.max_concurrent_tasks}
              onChange={(e) => set("max_concurrent_tasks", Math.max(1, Math.min(50, Number(e.target.value) || 1)))}
            />
            <TextField label={t("project.timezone")} value={form.timezone} onChange={(e) => set("timezone", e.target.value)} />
            <SelectField label={t("contentLanguage.projectLabel")} hint={t("contentLanguage.projectHint")} value={form.content_language} onChange={(e) => set("content_language", e.target.value)} data-testid="project-content-language">
              <option value="">{t("contentLanguage.inherit")}</option>
              {contentLanguageChoices(form.content_language || null).map((c) => (
                <option key={c.tag} value={c.tag}>
                  {c.label}
                </option>
              ))}
            </SelectField>
          </div>
          <label className="choice">
            <input type="checkbox" checked={form.auto_execute} onChange={(e) => set("auto_execute", e.target.checked)} />
            <span>
              {t("project.autoExecute")}
              <span className="field-hint" style={{ display: "block" }}>
                {t("project.autoExecuteHint")}
              </span>
            </span>
          </label>
        </div>
        <div className="dialog-footer">
          <span className="field-hint" style={{ marginRight: "auto" }}>
            {t("projectSettings.version", { n: baseVersion })}
          </span>
          <Button onClick={() => setForm(fromProject(project))} disabled={!dirty || busy}>
            {t("common.discard")}
          </Button>
          <Button type="submit" variant="primary" loading={busy} disabled={!dirty || conflict}>
            {t("common.save")}
          </Button>
        </div>
      </form>
      <div className="stack">
        <TestCommandCard project={project} workspaces={(detail.data as { workspaces?: WorkspaceRow[] } | undefined)?.workspaces} onSaved={onSaved} />
        {/* D37.2: the dependency install (prepare) command, set by a person. */}
        <DependenciesCard project={project} workspaces={(detail.data as { workspaces?: WorkspaceRow[] } | undefined)?.workspaces} onSaved={onSaved} />
        <ReadinessCheck projectId={project.id} stored={detail} />
        <section className="card card--pad stack" aria-labelledby="env-title">
          <h2 id="env-title" className="card-title">
            {t("projectSettings.environments")}
          </h2>
          <p className="muted">{t("projectSettings.environmentsBody")}</p>
          <UnavailableTag />
        </section>
      </div>
    </div>
    <ProjectRoutingPanel projectId={project.id} />
    <DeleteProjectSection projectId={project.id} projectName={project.name} />
    </div>
  );
}
